import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Book, Plus, Search, Trash2, X, Save,
    ChevronRight, Layout, Type, AlignLeft,
    Image as ImageIcon, Link as LinkIcon,
    Upload, FileJson, AlertCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TutorialsSection = ({
    filteredTutorials,
    searchQuery,
    setSearchQuery,
    showAddModal,
    setShowAddModal,
    newTutorial,
    setNewTutorial,
    addTutorial,
    deleteTutorial,
    handleJsonImport,
    addTopic,
    updateTopic,
    removeTopic,
    isMobile
}) => {
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image', 'code-block'],
            ['clean']
        ],
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
                        <Book className="text-primary" /> CodeCampus Tutorials
                    </h3>
                    <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mt-1 opacity-70">
                        Create and manage educational roadmaps and tutorials
                    </p>
                </div>

                <div className="flex items-center gap-4 self-stretch md:self-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                        <Input
                            placeholder="Search tutorials..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="pl-10 rounded-xl bg-card/40 border-border/40 font-bold text-sm"
                        />
                    </div>
                    <Button
                        onClick={() => setShowAddModal(true)}
                        className="rounded-xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] px-6 shadow-lg shadow-primary/20 gap-2"
                    >
                        <Plus size={16} /> Create New
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {filteredTutorials.map((tut, i) => (
                    <motion.div
                        key={tut.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <Card className="p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2rem] hover:border-primary/20 transition-all group">
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                                <div className="flex items-center gap-5 flex-1 min-w-0">
                                    <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
                                        <Book size={28} />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-lg font-black text-foreground tracking-tight truncate">{tut.title}</h4>
                                        <p className="text-muted-foreground text-sm font-bold opacity-70 truncate max-w-md">{tut.description}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                                    <Badge variant="secondary" className="bg-secondary/40 text-muted-foreground border-border/60 font-black text-[10px] uppercase tracking-widest px-3 py-1 rounded-lg">
                                        {tut.category || 'General'}
                                    </Badge>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteTutorial(tut.id)}
                                        className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                ))}

                {filteredTutorials.length === 0 && (
                    <div className="text-center py-20 bg-card/40 backdrop-blur-xl rounded-[2.5rem] border-2 border-dashed border-border/60 opacity-40">
                        <Book size={48} className="mx-auto mb-4" />
                        <div className="font-black uppercase tracking-widest text-xs">No tutorials found</div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showAddModal && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowAddModal(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-lg"
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-5xl max-h-full overflow-y-auto bg-card border border-border/40 rounded-[2.5rem] shadow-2xl p-6 md:p-10 no-scrollbar"
                        >
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h3 className="text-2xl md:text-3xl font-black text-foreground tracking-tighter">Create Roadmap</h3>
                                    <p className="text-muted-foreground text-sm font-bold opacity-70">Build a hierarchical learning journey</p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowAddModal(false)}
                                    className="w-12 h-12 rounded-full border border-border/60 hover:rotate-90 transition-all duration-300"
                                >
                                    <X size={24} />
                                </Button>
                            </div>

                            <div className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                            <Type size={12} /> Tutorial Title *
                                        </label>
                                        <Input
                                            placeholder="e.g. Mastering React Hooks"
                                            value={newTutorial.title}
                                            onChange={e => setNewTutorial({ ...newTutorial, title: e.target.value })}
                                            className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                            <Layout size={12} /> Category *
                                        </label>
                                        <select
                                            className="w-full rounded-2xl p-3 bg-background/50 border border-border/60 font-bold text-sm outline-none focus:ring-2 focus:ring-primary/20 appearance-none h-[50px]"
                                            value={newTutorial.category}
                                            onChange={e => setNewTutorial({ ...newTutorial, category: e.target.value })}
                                        >
                                            {['General', 'Programming', 'Web Dev', 'DSA', 'Database', 'Networks', 'AI/ML', 'Cloud', 'Roadmaps'].map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        <AlignLeft size={12} /> Brief Description *
                                    </label>
                                    <Textarea
                                        placeholder="Provide a concise summary of the learning journey..."
                                        value={newTutorial.description}
                                        onChange={e => setNewTutorial({ ...newTutorial, description: e.target.value })}
                                        className="rounded-2xl min-h-[100px] bg-background/50 border-border/60 font-bold tracking-tight"
                                    />
                                </div>

                                <Card className="p-6 bg-primary/5 border-dashed border-2 border-primary/20 rounded-3xl group">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                                                <FileJson size={24} />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-black uppercase tracking-wider text-foreground">Import Roadmap JSON</h4>
                                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-60">Upload roadmap.sh compatible structure</p>
                                            </div>
                                        </div>
                                        <label className="cursor-pointer">
                                            <Input type="file" accept=".json" onChange={handleJsonImport} className="hidden" />
                                            <div className="px-6 py-3 bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] rounded-xl shadow-lg shadow-primary/20 flex items-center gap-2 hover:scale-105 transition-transform">
                                                <Upload size={14} /> Choose File
                                            </div>
                                        </label>
                                    </div>
                                </Card>

                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                            <Book size={12} /> Roadmap Topics ({newTutorial.topics.length})
                                        </label>
                                        <Button
                                            variant="outline"
                                            onClick={addTopic}
                                            className="rounded-xl border-border/60 font-black text-[10px] uppercase tracking-widest gap-2 bg-background/50"
                                        >
                                            <Plus size={14} /> Add Topic
                                        </Button>
                                    </div>

                                    <div className="space-y-8">
                                        {newTutorial.topics.map((topic, index) => (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className="relative p-6 md:p-8 bg-background/40 border border-border/60 rounded-[2rem] space-y-6"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-secondary/50 border border-border/60 flex items-center justify-center font-black text-xs">
                                                        {index + 1}
                                                    </div>
                                                    <Input
                                                        placeholder={`Topic ${index + 1} Title`}
                                                        value={topic.title}
                                                        onChange={e => updateTopic(index, 'title', e.target.value)}
                                                        className="flex-1 rounded-xl py-6 bg-background/50 border-border/60 font-black tracking-tight"
                                                    />
                                                    {newTutorial.topics.length > 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeTopic(index)}
                                                            className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"
                                                        >
                                                            <Trash2 size={20} />
                                                        </Button>
                                                    )}
                                                </div>

                                                <div className="space-y-4">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                                        Content Editor
                                                    </label>
                                                    <div className="bg-white rounded-2xl border border-border/60 overflow-hidden text-black min-h-[350px]">
                                                        <ReactQuill
                                                            theme="snow"
                                                            value={topic.content}
                                                            onChange={val => updateTopic(index, 'content', val)}
                                                            modules={quillModules}
                                                            className="h-[300px]"
                                                        />
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <Button
                                        onClick={() => setShowAddModal(false)}
                                        variant="outline"
                                        className="flex-1 py-7 rounded-2xl border-border/60 font-black uppercase tracking-widest text-[10px] hover:bg-secondary/50"
                                    >
                                        Discard Changes
                                    </Button>
                                    <Button
                                        onClick={addTutorial}
                                        className="flex-[2] py-7 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] shadow-xl shadow-primary/30 flex items-center gap-2"
                                    >
                                        <Save size={18} /> Publish to CodeCampus
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default TutorialsSection;
